/**
 * Environment-aware analytics configuration.
 *
 * Reads from the centralized env config (src/config/env.ts).
 * When migrating to Next.js only the env config file changes.
 */

import { env as appEnv } from '@/config/env';

export type AnalyticsEnvironment = 'development' | 'staging' | 'production';

export interface AnalyticsConfig {
  environment: AnalyticsEnvironment;
  gtmId: string;
  gtmAuth?: string;
  gtmPreview?: string;
  ga4MeasurementId: string;
  /** Meta (Facebook) Pixel ID — informational; pixel loads via GTM when marketing consent is granted (REK-1393). */
  metaPixelId: string;
  posthogKey: string;
  posthogHost: string;
  appVersion: string;
  brazeSdkApiKey: string;
  brazeSdkEndpoint: string;
}

function detectEnvironment(): AnalyticsEnvironment {
  const explicit = appEnv.analyticsEnv;
  if (explicit === 'production' || explicit === 'staging' || explicit === 'development') {
    return explicit;
  }
  if (typeof window !== 'undefined') {
    const host = window.location.hostname;
    if (host === 'www.reklaimyou.com' || host === 'reklaimyou.com') return 'production';
    if (host === 'beta.reklaimyou.com') return 'staging';
  }
  return 'development';
}

let _config: AnalyticsConfig | null = null;

export function getAnalyticsConfig(): AnalyticsConfig {
  if (_config) return _config;

  const env = detectEnvironment();

  _config = {
    environment: env,
    gtmId: appEnv.gtmId || '',
    gtmAuth: appEnv.gtmAuth || undefined,
    gtmPreview: appEnv.gtmPreview || undefined,
    ga4MeasurementId: appEnv.gaMeasurementId || '',
    metaPixelId: appEnv.metaPixelId || '',
    posthogKey: appEnv.posthogKey || '',
    posthogHost: appEnv.posthogHost || 'https://k.reklaimyou.com',
    appVersion: appEnv.appVersion || '0.0.0',
    brazeSdkApiKey: appEnv.brazeApiKey || '',
    brazeSdkEndpoint: appEnv.brazeSdkEndpoint || '',
  };

  return _config;
}
